import { createSelector } from 'reselect'
import { RootState, IFeatureFlagsMap } from 'constants/interfaces'
import { FEATURE_FLAGS, FeatureFlagKey } from 'constants/featureFlags'

const featureFlagsSelector = (state: RootState) => state.config.retailConfig?.feature_flag

const isFlagEnabled = (flags: IFeatureFlagsMap | undefined, key: FeatureFlagKey): boolean => {
  const val = flags?.[key]
  if (val == null) return false
  if (typeof val === 'object') return (val as Record<string, unknown>)['perm'] === 1
  return val === 1
}

// Boolean map: { prod_perf: true/false, ... } — use in components with multiple flag checks
export const featureFlagsBooleanMapSelector = createSelector(
  featureFlagsSelector,
  (flags) =>
    Object.fromEntries(Object.values(FEATURE_FLAGS).map((key) => [key, isFlagEnabled(flags, key)])) as Record<
      FeatureFlagKey,
      boolean
    >
)

// Factory selector — use when a single component needs exactly one flag (e.g. route guards)
export const makeIsFeatureEnabledSelector = (flagKey: FeatureFlagKey) =>
  createSelector(featureFlagsSelector, (flags) => isFlagEnabled(flags, flagKey))

// Factory selector for flags that return an object — caller provides the expected shape via generic T
export const makeFlagObjectSelector = <T extends object>(flagKey: FeatureFlagKey) =>
  createSelector(featureFlagsSelector, (flags): T | null => {
    const val = flags?.[flagKey]
    return typeof val === 'object' ? (val as T) : null
  })
